// RoomsPage.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import {
    Wifi, Coffee, Wind, Droplets, Leaf, Users, BedDouble, ShowerHead, Sun, ChevronRight
} from 'lucide-react';

// Images
import heroBg from '../assets/Mahi/79.jpeg';
import mainRoomImg from '../assets/Mahi/36.jpeg';     // main hero-like image
import slide1 from '../assets/Mahi/39.jpeg';
import slide2 from '../assets/Mahi/50.jpeg';
import slide3 from '../assets/Mahi/44.jpeg';
import slide4 from '../assets/Mahi/45.jpeg';
import slide5 from '../assets/Mahi/117.jpeg';
import slide6 from '../assets/Mahi/52.jpeg';
import slide7 from '../assets/Mahi/51.jpeg';
import slide8 from '../assets/Mahi/79.jpeg';

const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 1, ease: "easeOut" } }
};

const staggerContainer = {
    hidden: { opacity: 1 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.15, delayChildren: 0.25 }
    }
};

const childFade = {
    hidden: { opacity: 0, y: 25 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9 } }
};

export default function RoomsPage() {
    const { t } = useTranslation('rooms');
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Single room type – Deluxe Room
    const deluxeRoom = {
        name: t('deluxeRoomName'),
        category: t('deluxeRoomCategory'),
        description: t('deluxeRoomDesc'),
        size: "45 m²",
        occupancy: "2 adults",
        basePrice: 180, // starting from
        mainImage: mainRoomImg,
        gallery: [slide1, slide2, slide3, slide4, slide5, slide6, slide7, slide8],
        amenities: [
            { icon: BedDouble, label: t('kingSizeBed') },
            { icon: ShowerHead, label: t('rainShower') },
            { icon: Wifi, label: t('highSpeedWifi') },
            { icon: Coffee, label: t('herbalTeaCoffee') },
            { icon: Wind, label: t('individuallyControlledAC') },
            { icon: Droplets, label: t('premiumOrganicToiletries') },
            { icon: Leaf, label: t('dailyFreshFlowers') },
            { icon: Sun, label: t('beachView') },
        ],
        included: [
            "Daily housekeeping",
            "Morning yoga access",
            "Complimentary mineral water",
            "In-room safe",
            "Hair dryer & bathrobes",
            "24-hour concierge"
        ]
    };

    // Gallery slider settings
    const sliderSettings = {
        dots: false,
        infinite: true,
        speed: 800,
        slidesToShow: 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 3500,
        pauseOnHover: true,
        arrows: false,
        responsive: [
            { breakpoint: 1024, settings: { slidesToShow: 2 } },
            { breakpoint: 640, settings: { slidesToShow: 1 } }
        ]
    };

    return (
        <div className="bg-[#F8F5ED] text-[#1A2F2A] min-h-screen font-sans">
            {/* ─── HERO ──────────────────────────────────────────────── */}
            <section className="relative h-[70vh] min-h-[520px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={heroBg}
                        alt="Resort aerial view"
                        className="w-full h-full object-cover brightness-[0.78] scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60" />
                </div>

                <div className="relative z-10 text-center px-5 sm:px-8 max-w-5xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.3 }}
                    >
                        <span className="inline-block text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium mb-5">
                            Mahi Beach Ayurveda
                        </span>

                        <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-serif font-light text-white leading-tight drop-shadow-2xl">
                            {t('heroTitle')}
                        </h1>

                        <p className="mt-7 text-xl sm:text-2xl md:text-3xl font-light text-[#E8C07D] tracking-wide">
                            {t('heroSubtitle')}
                        </p>

                        <div className="mt-12 inline-flex items-center gap-10">
                            <div className="h-px w-16 bg-[#E8C07D]/60" />
                            <span className="text-[#E8C07D] uppercase tracking-widest text-sm font-medium">
                                {t('deluxeRoomCategory')}
                            </span>
                            <div className="h-px w-16 bg-[#E8C07D]/60" />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ─── MAIN ROOM SECTION (sm only) ───────────────────────── */}
            <section className="py-16 sm:py-20 bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] block md:hidden">
                <div className="container mx-auto px-5 sm:px-8 max-w-7xl">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="space-y-6"
                    >
                        <div className="text-center">
                            <span className="text-[#E8C07D] uppercase tracking-widest text-xs font-medium">
                                {t('signatureAccommodation')}
                            </span>
                            <h2 className="mt-3 text-3xl sm:text-4xl font-serif font-light text-[#1A2F2A]">
                                {deluxeRoom.name}
                            </h2>
                        </div>

                        <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]">
                            <img
                                src={deluxeRoom.mainImage}
                                alt={deluxeRoom.name}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                        </div>

                        <p className="text-base sm:text-lg leading-relaxed text-[#2D3F3A]/90 font-light text-center">
                            {deluxeRoom.description}
                        </p>

                        <div className="flex flex-wrap justify-center gap-4 text-base">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#E8C07D]/10 flex items-center justify-center">
                                    <BedDouble className="w-5 h-5 text-[#E8C07D]" />
                                </div>
                                <span>{deluxeRoom.size}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#E8C07D]/10 flex items-center justify-center">
                                    <Users className="w-5 h-5 text-[#E8C07D]" />
                                </div>
                                <span>{deluxeRoom.occupancy}</span>
                            </div>
                        </div>

                        <div className="pt-2 flex flex-col gap-4">
                            <button
                                onClick={() => navigate('/search')}
                                className="bg-[#1A2F2A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all shadow-lg flex items-center justify-center gap-2 group"
                            >
                                <span>{t('bookNow')}</span>
                                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <button
                                onClick={() => navigate('/rooms/deluxe')}
                                className="border-2 border-[#1A2F2A]/70 text-[#1A2F2A] px-8 py-4 rounded-full font-medium hover:bg-[#1A2F2A]/5 transition-all"
                            >
                                {t('viewFullDetails')}
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ─── MAIN ROOM SECTION ─────────────────────────────────── */}
            <section className="py-20 md:py-28 lg:py-36 bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5] hidden md:block">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
                    >
                        {/* Images + gallery preview */}
                        <motion.div variants={fadeInUp} className="relative order-2 lg:order-1">
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] lg:aspect-[5/4]">
                                <img
                                    src={deluxeRoom.mainImage}
                                    alt={deluxeRoom.name}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-70" />
                            </div>

                            {/* Small floating preview cards */}
                            <div className="absolute -bottom-10 -right-6 sm:-right-12 hidden sm:block w-48 sm:w-64 aspect-square rounded-2xl overflow-hidden shadow-2xl border-8 border-white/90 rotate-6 hover:rotate-0 transition-transform">
                                <img
                                    src={slide3}
                                    alt="Room detail"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </motion.div>

                        {/* Content */}
                        <motion.div variants={staggerContainer} className="order-1 lg:order-2 space-y-8">
                            <div>
                                <span className="text-[#E8C07D] uppercase tracking-widest text-sm font-medium">
                                    {t('signatureAccommodation')}
                                </span>
                                <h2 className="mt-3 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                                    {deluxeRoom.name}
                                </h2>
                            </div>

                            <p className="text-lg md:text-xl leading-relaxed text-[#2D3F3A]/90 font-light">
                                {deluxeRoom.description}
                            </p>

                            <div className="flex flex-wrap gap-6 text-lg">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-[#E8C07D]/10 flex items-center justify-center">
                                        <BedDouble className="w-5 h-5 text-[#E8C07D]" />
                                    </div>
                                    <span>{deluxeRoom.size}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-[#E8C07D]/10 flex items-center justify-center">
                                        <Users className="w-5 h-5 text-[#E8C07D]" />
                                    </div>
                                    <span>{deluxeRoom.occupancy}</span>
                                </div>

                            </div>

                            <div className="pt-6 flex flex-col sm:flex-row gap-5">
                                <button
                                    onClick={() => navigate('/search')}
                                    className="bg-[#1A2F2A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all shadow-lg flex items-center justify-center gap-2 group"
                                >
                                    <span>{t('bookNow')}</span>
                                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button
                                    onClick={() => navigate('/rooms/deluxe')}
                                    className="border-2 border-[#1A2F2A]/70 text-[#1A2F2A] px-8 py-4 rounded-full font-medium hover:bg-[#1A2F2A]/5 transition-all"
                                >
                                    {t('viewFullDetails')}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* ─── AMENITIES GRID ────────────────────────────────────── */}
            <section className="py-20 md:py-28 bg-[#F2EEE5]">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="text-center mb-16 md:mb-20"
                    >
                        <motion.span variants={childFade} className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                            {t('inRoomComfort')}
                        </motion.span>
                        <motion.h2 variants={childFade} className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('deluxeRoomAmenities')}
                        </motion.h2>
                        <motion.div variants={childFade} className="h-1 w-20 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <motion.div
                        variants={staggerContainer}
                        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
                    >
                        {deluxeRoom.amenities.map((item, i) => (
                            <motion.div
                                key={i}
                                variants={childFade}
                                className="bg-white/70 backdrop-blur-sm p-6 md:p-8 rounded-2xl border border-[#E8C07D]/10 hover:border-[#E8C07D]/40 hover:shadow-xl transition-all group"
                            >
                                <div className="w-14 h-14 rounded-full bg-[#E8C07D]/10 flex items-center justify-center mb-5 group-hover:bg-[#E8C07D] transition-colors">
                                    <item.icon className="w-7 h-7 text-[#E8C07D] group-hover:text-white transition-colors" />
                                </div>
                                <p className="text-lg font-light text-[#2D3F3A]/90 group-hover:text-[#1A2F2A]">
                                    {item.label}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* ─── GALLERY SLIDER ────────────────────────────────────── */}
            <section className="py-20 md:py-28 bg-gradient-to-b from-[#F2EEE5] to-[#F8F5ED]">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="text-center mb-12 md:mb-16"
                    >
                        <motion.h3 variants={childFade} className="text-4xl md:text-5xl font-serif font-light text-[#1A2F2A]">
                            {t('momentsInDeluxeRoom')}
                        </motion.h3>
                    </motion.div>

                    <Slider {...sliderSettings}>
                        {deluxeRoom.gallery.map((img, idx) => (
                            <div key={idx} className="px-3">
                                <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3] group">
                                    <img
                                        src={img}
                                        alt={`Deluxe Room view ${idx + 1}`}
                                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-70" />
                                </div>
                            </div>
                        ))}
                    </Slider>
                </div>
            </section>

            {/* ─── FINAL CTA ─────────────────────────────────────────── */}
            <section className="relative py-24 md:py-32 overflow-hidden">
                <div className="absolute inset-0 bg-[#1A2F2A]">
                    <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_70%,#E8C07D_0%,transparent_50%)]" />
                </div>

                <div className="relative max-w-4xl mx-auto px-6 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                    >
                        <Leaf className="w-14 h-14 text-[#E8C07D] mx-auto mb-8" />
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-white mb-6">
                            {t('beginHealing')}
                        </h2>
                        <p className="text-xl text-[#E8C07D]/90 mb-10 max-w-2xl mx-auto font-light">
                            {t('experienceAyurvedicLiving')}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-5 justify-center">
                            <button
                                onClick={() => navigate('/search')}
                                className="bg-[#E8C07D] text-[#1A2F2A] px-10 py-5 rounded-full font-medium text-lg hover:bg-white transition-all shadow-xl"
                            >
                                {t('reserveYourStay')}
                            </button>
                            <a
                                href="https://wa.me/+94766111678"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="border-2 border-[#E8C07D] text-[#E8C07D] px-10 py-5 rounded-full font-medium text-lg hover:bg-[#E8C07D]/10 transition-all"
                            >
                                {t('speakAdvisor')}
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}